import { faPlus } from '@fortawesome/pro-light-svg-icons'
import { OrderTableData } from '@teinor/erp/prices/quote/totals'
import { financialRowTypesInversed } from '@teinor/erp/types/company/order/general'
import { useCallback } from 'react'
import { ButtonSecondary } from '../../../../../../../../../baseComponents/Button'
import { ModalBigCentered } from '../../../../../../../../../baseComponents/Modal'
import { ModalEditComponentProps } from '../../../../../../../../../baseComponents/ModalsLayouts/types'
import TwinIcon from '../../../../../../../../../baseComponents/TwinIcon'
import { OrderHeaderURLs } from '../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import TableWithoutVirtualListing from '../../../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { ColumnTableSchema } from '../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import TwinTrans from '../../../../../../../../../baseComponents/TwinTrans'
import { TextField } from '../../../../../../../../../forms/TextField'
import { twinFetchPostJSON } from '../../../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../../../utils/globals/dictionary'
import { getCurrencySymbol } from '../../../../../../../../../utils/globals/price'
import useTwinTranslation from '../../../../../../../../../utils/hooks/useTwinTranslation'
import { SetCompleteDataTy } from '../../../../../types'
import { OrderFinancialRowExtended } from '../../../types'
import { parseFinancialRowsOrderLayout } from './functions'

export interface ModalFinancialRowsOrderProps extends ModalEditComponentProps {
    tableData?: OrderTableData
    isReadOnly?: boolean
    financialRowTypesDict: TwinDictionary
    setCompleteData: SetCompleteDataTy
    headerData: TwinDictionary
    orderHeaderURLs: OrderHeaderURLs
    OrderId: number
}
interface ModalFinancialRowsOrderReadOnlyTableProps extends ModalFinancialRowsOrderProps {
    titleReadOnly: string
}

export const ModalFinancialRowsOrderReadOnlyTable: React.FC<ModalFinancialRowsOrderReadOnlyTableProps> = ({ headerData, setOpened, financialRowTypesDict, titleReadOnly }) => {
    const { t } = useTwinTranslation()
    const financialRows = parseFinancialRowsOrderLayout(headerData?.OrderFinancialRows, financialRowTypesDict)
    const currencyHeaderId = headerData.CurrencyId
    const items: TwinDictionary = { 0: { name: '%' }, 1: { name: getCurrencySymbol(currencyHeaderId) } }

    const columns: ColumnTableSchema<OrderFinancialRowExtended> = [
        {
            id: 'discount',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            customRender: (value, rowData) => <TextField label='' value={rowData?.quantity + ' ' + items?.[rowData?.calculationType]?.name} className='text-start' />
        },
        {
            id: 'financialRowType',
            dataKey: 'financialRowType',
            label: t('type', 'Tipo'),
            customRender: (value, rowData) => {
                const financialRowType = rowData?.financialRowType === financialRowTypesInversed['discountForTotalFooter'] ? financialRowTypesInversed['discountWithCosts'] : rowData?.financialRowType
                return <TextField label='' value={financialRowTypesDict?.[financialRowType]?.name} className='text-start' />
            }
        },
        {
            id: 'beforeTax',
            dataKey: 'beforeTax',
            label: '',
            customRender: (value, rowData) => <TextField label='' value={rowData?.beforeTax ? t('beforeTax', 'Antes de impuestos') : t('afterTax', 'Después de impuestos')} className='text-start' />
        }
    ]

    return (
        <ModalBigCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_order_sell_layout modal_add_edit_discounts_recharges flex_height'>
            <h2 className='flex justify-start regular20'>{titleReadOnly}</h2>
            <div className='mt-20 flex flex-auto flex-col'>
                <div className='flex-auto overflow-visible'>
                    <TableWithoutVirtualListing data={financialRows} columns={columns} className='min-h-200 add_discount_recharge_table flex_height' />
                </div>
                <div className='flex justify-end gap-x-33'>
                    <ButtonSecondary onClick={() => setOpened(null)} className='regular14 mt-10'>
                        <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                    </ButtonSecondary>
                </div>
            </div>
        </ModalBigCentered>
    )
}


interface ModalFinancialRowsOrderEditTableProps extends ModalFinancialRowsOrderProps {
    titleFinancialEdit: string
    myColumns: ColumnTableSchema<OrderFinancialRowExtended>
    addTotalDiscountRow: (rowIndex: number) => void
}

export const ModalFinancialRowsOrderEditTable: React.FC<ModalFinancialRowsOrderEditTableProps> = ({ headerData, setOpened, financialRowTypesDict, titleFinancialEdit, myColumns, OrderId, ...rest }) => {
    const financialRows = parseFinancialRowsOrderLayout(headerData?.OrderFinancialRows, financialRowTypesDict)
    const { addRow } = useModalFinancialRowsOrderEditLogic({ headerData, financialRowTypesDict, OrderId, ...rest })
    return (
        <ModalBigCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_order_sell_layout modal_add_edit_discounts_recharges flex_height'>
            <h2 className='flex justify-start regular20'>{titleFinancialEdit}</h2>
            <div className='mt-20 flex flex-auto flex-col'>
                <div className='min-h-200 overflow-auto'>
                    <div className='max-h-300 overflow-visible'>
                        <TableWithoutVirtualListing data={financialRows} disableScroll={true} columns={myColumns} className='add_discount_recharge_table' />
                        <div className='cursor-pointer text-center bg-gray-EF py-4 w-full text-gray-51 ' onClick={addRow}>
                            <TwinIcon className='mr-10' icon={faPlus} />
                            <TwinTrans transKey='addRow'>Añadir fila</TwinTrans>
                        </div>
                    </div>
                </div>
                <div className='flex items-center gap-x-10 justify-end mt-20'>
                    <ButtonSecondary onClick={() => setOpened(null)}>
                        <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                    </ButtonSecondary>
                </div>
            </div>
        </ModalBigCentered>
    )
}

interface ModalFinancialRowsOrderEditLogicProps {
    headerData: TwinDictionary
    setCompleteData: SetCompleteDataTy
    orderHeaderURLs: OrderHeaderURLs
    tableData?: OrderTableData | undefined
    financialRowTypesDict: TwinDictionary
    OrderId: number
    addTotalDiscountRow: (rowIndex: number) => void
}

const useModalFinancialRowsOrderEditLogic = ({ setCompleteData, orderHeaderURLs, financialRowTypesDict, OrderId, addTotalDiscountRow }: ModalFinancialRowsOrderEditLogicProps) => {
    const parsedDefaultFinancialRowType = parseInt(Object.keys(financialRowTypesDict)[0])

    const addRow = useCallback(async () => {
        const financialRow = await twinFetchPostJSON(orderHeaderURLs.createFinancialRow, {
            quantity: 0,
            OrderId,
            financialRowType: parsedDefaultFinancialRowType
        })
        let rowIndex = 0
        setCompleteData((old) => {
            if (!old) {
                return null
            }
            const myData = JSON.parse(JSON.stringify(old))
            myData?.header?.OrderFinancialRows?.push(financialRow)
            rowIndex = myData?.header?.OrderFinancialRows?.length - 1
            return { ...myData }
        })
        addTotalDiscountRow(rowIndex)
    }, [OrderId, orderHeaderURLs.createFinancialRow, setCompleteData, parsedDefaultFinancialRowType, addTotalDiscountRow])

    return { addRow }
}